import { Component, ViewChild, ElementRef, Input, Output, EventEmitter, OnChanges, OnInit, SimpleChanges, ChangeDetectorRef } from '@angular/core';
import { HttpClient } from '@angular/common/http';
import { MatCard, MatCardContent, MatCardHeader, MatCardTitle } from '@angular/material/card';
import { MatIconButton, MatFabButton } from '@angular/material/button';
import { MatTooltip } from '@angular/material/tooltip';
import { MatIcon } from '@angular/material/icon';


interface VideoTopic{
  indexID: number;
  item_CODE: string;
  timestamp: string;
  topic: string;
}

@Component({
    selector: 'app-videoviewer',
    templateUrl: './videoviewer.component.html',
    styleUrls: ['./videoviewer.component.css'],
    imports: [MatCard, MatCardContent, MatIconButton, MatTooltip, MatIcon, MatFabButton, MatCardHeader, MatCardTitle]
})
export class VideoViewerComponent implements OnChanges, OnInit {
  @Input() videoUrl: string = ''; // Input for the video URL
  @Input() videoCODE: string = ''; // Input for the video CODE
  @Input() videoTitle: string = 'Video Player'; // Input for the video title

  @Output() back = new EventEmitter<void>(); // Output event to go back to the list

  @ViewChild('videoPlayer', { static: false }) videoPlayerRef!: ElementRef<HTMLVideoElement>;

  // Video player state variables
  isPlaying: boolean = false;
  currentSpeed: number = 1.0; // Default playback speed
  availableSpeeds: number[] = [0.5, 1.0, 1.25, 1.5, 2.0]; // Options for speed control

  // array of timestamps for video = videoCODE;
  isLoading = false;
  videoTopics: VideoTopic[] = [];



  constructor(private http: HttpClient, private cdr: ChangeDetectorRef) { }

  ngOnInit(): void {
    // get topic list for video from videoCODE
    // post param {item_CODE: this.videoCODE}
    // load table, make rows clickable to jump to timestamp

    this.isLoading = true;
    this.http.post<VideoTopic[]>('api/members/getMemberVideoTopicIndexes.php', {item_CODE: this.videoCODE}).subscribe({
      next: (data) => {
        this.videoTopics = data;
        this.isLoading = false;
        this.cdr.markForCheck();
      },
      error: (error) => {
        console.error('Error fetching products:', error);
        this.isLoading = false;
        this.cdr.markForCheck();
      }
    });   

  }


  /**
   * Lifecycle hook that detects changes to input properties.
   * Used here to load new video when videoUrl changes.
   */
  ngOnChanges(changes: SimpleChanges): void {
    if (changes['videoUrl'] && this.videoPlayerRef) {
      // Reload video when the URL changes
      this.videoPlayerRef.nativeElement.load();
      this.isPlaying = false; // Reset play state
      this.currentSpeed = 1.0; // Reset speed
    }
  }

  /**
   * Toggles play/pause state of the video.
   */
  togglePlayPause(): void {
    const video = this.videoPlayerRef.nativeElement;
    if (video.paused) {
      video.play();
      this.isPlaying = true;
    } else {
      video.pause();
      this.isPlaying = false;
    }
  }

  /**
   * Skips forward by a specified number of seconds.
   * @param seconds The number of seconds to fast forward.
   */
  fastForward(seconds: number): void {
    
    const video = this.videoPlayerRef.nativeElement;
     video.currentTime += seconds;
  }

  /**
   * Skips backward by a specified number of seconds.
   * @param seconds The number of seconds to rewind.
   */
  rewind(seconds: number): void {
    const video = this.videoPlayerRef.nativeElement;
    video.currentTime = Math.max(0, video.currentTime - seconds);
  }

  /**
   * Restarts the video from the beginning.
   */
  replay(): void {
    const video = this.videoPlayerRef.nativeElement;
    video.currentTime = 0;
    video.play();
    this.isPlaying = true;
  }

  /**
   * Changes the playback speed of the video.
   * @param speed The desired playback speed (e.g., 0.5, 1.0, 1.5).
   */
  changeSpeed(speed: number): void {
    const video = this.videoPlayerRef.nativeElement;
    video.playbackRate = speed;
    this.currentSpeed = speed;
  }

  /**
   * Toggles full-screen mode for the video player.
   * Handles browser-specific full-screen APIs.
   */
  toggleFullScreen(): void {
    const video = this.videoPlayerRef.nativeElement;
    if (video.requestFullscreen) {
      video.requestFullscreen();
    } else if ((video as any).mozRequestFullScreen) { /* Firefox */
      (video as any).mozRequestFullScreen();
    } else if ((video as any).webkitRequestFullscreen) { /* Chrome, Safari & Opera */
      (video as any).webkitRequestFullscreen();
    } else if ((video as any).msRequestFullscreen) { /* IE/Edge */
      (video as any).msRequestFullscreen();
    }
  }

  /**
   * Emits the 'back' event to the parent component.
   */
  goBack(): void {
    this.back.emit();
  }

  timeStringToSeconds(timeStr: string): number {
    const parts = timeStr.split(':').map(Number);
    // Formula: (hours * 3600) + (minutes * 60) + seconds + (hundredths / 100)
    return (parts[0] * 3600) + (parts[1] * 60) + parts[2] + (parts[3] / 100);
  }

  playTimestamp(timestampString: string): void {

    const targetTimeInSeconds = this.timeStringToSeconds(timestampString);

    const video = this.videoPlayerRef.nativeElement;
    video.currentTime = targetTimeInSeconds;

  }

}
